import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import filter from 'lodash/filter';

import includes from 'lodash/includes';
import get from 'lodash/get';
import omit from 'lodash/omit';
import isObject from 'lodash/isObject';

import has from 'lodash/has';
import isString from 'lodash/isString';
import set from 'lodash/set';
import without from 'lodash/without';

import { matchPath } from 'react-router';
import getTld from 'tld.js';
import { normalizeString as normalizeMakeModel } from '@dmm/lib-common/lib/formatting';
import {
  normalizeString,
  translateMake,
  unhyphenateUrlComponents
} from '../commonHelper';
import {
  getCurrencyFormat,
  getCurrencySymbol,
  getLengthSymbol,
  getRadius
} from '../uomHelper';
import { toggleSubFacet } from '../multiFacetHelper';
import { getBoatsConstants } from '../../constants/boats/index';
import { getConfig } from '../../config/portal';
import {
  getDefaultI18Service,
  getFormatMessageFunction
} from '../../tppServices/tppDi';
import { getDomain } from '../../tppServices/translations/helpers';
import {
  getBoatConstantsFromI18n,
  getMappedValueFromI18n
} from '../../tppServices/translations/constants';
import { urlParamsParserFunctions } from './constantsParsers';
import { sharedHyphenate, slugify, unslugify } from './shared';
import { getMessages } from '../../tppServices/translations/messages';
import { shortenB64latlon } from '../../tppServices/crossEnvHelpers';
import { definedValue } from '../index';
import isEqual from 'lodash/isEqual';
import { boatsUrlGenerate } from './urlPathParse';

import { getSearchUrlPattern } from './patternFinders';

// Alias to keep the old code working
export const generateSearchPath = boatsUrlGenerate;

export const parseSearchBoatsAppParams = (
  url,
  isBranded = false,
  config,
  i18nService
) => {
  if (!url) {
    return {};
  }
  const searchPattern = getSearchUrlPattern(url, isBranded);
  const matched = matchPath(decodeURI(url), searchPattern);
  if (matched) {
    let { params } = matched;
    return getDefaultParams(params, config, i18nService);
  }
  return {};
};

// We should be able to cache this, but there are places where we are doing
// object comparisons, so we can not cache it, yet. We need to find those comparisons
// and refactor them to use the same object reference.
export const getDefaultParams = (params, config, i18nService) => {
  if (!config) {
    // TODO: remove this call when needed
    config = getConfig();
  }

  const boatConstants = getBoatConstantsFromI18n(i18nService);
  const t = getFormatMessageFunction(i18nService);
  const mappedValue = getMappedValueFromI18n(i18nService);
  const parsers = urlParamsParserFunctions(
    boatConstants,
    config,
    t,
    mappedValue,
    i18nService
  );
  const {
    parseByParams,
    parseCityParams,
    parseClassGroupParams,
    parseConditionParams,
    parseCountryParams,
    parseCurrencyParams,
    parseEngineNumberParams,
    parseEngineOneHoursParams,
    parseEngineTypeParams,
    parseFuelParams,
    parseHullParams,
    parseKeywordParams,
    parseLegacyMakeModelParams,
    parseLengthParams,
    parseMakeModelParams,
    parseModalParams,
    parseModelRangeParams,
    parseOwnerParams,
    parsePageParams,
    parsePostalCodeParams,
    parsePriceParams,
    parseRadiusParams,
    parseLatlonParams,
    parseRegionParams,
    parseRepParams,
    parseSortParams,
    parseSubdivisionParams,
    parseTypeAndClassParams,
    parseWorldRegionParams,
    parseYearParams,
    parsePlaceIdParams
  } = parsers;
  let parsed = {
    ...parseSubdivisionParams(params.subdivision),
    ...parseRegionParams(params.region),
    ...parseWorldRegionParams(params.worldRegion),
    ...parseCountryParams(params.country),
    ...parseCityParams(params.city),
    ...parsePostalCodeParams(params.postalCode),
    ...parseRadiusParams(params.radius),
    ...parseLatlonParams(params.latlon),
    ...parsePlaceIdParams(params.placeId),
    ...parseClassGroupParams(params.group),
    ...parseLegacyMakeModelParams(params.make, params.model),
    ...parseMakeModelParams(params.makeModel),
    ...parseModelRangeParams(params.modelRange),
    ...parseLengthParams(params.length),
    ...parseEngineTypeParams(params.engine),
    ...parsePageParams(params.page),
    ...parseSortParams(params.sort),
    ...parseConditionParams(params.condition),
    ...parseYearParams(params.year),
    ...parseFuelParams(params.fuelType),
    ...parseTypeAndClassParams(params.types, params.classes),
    ...parsePriceParams(params.price),
    ...parseCurrencyParams(params.currency),
    ...parseModalParams(params.modal),
    ...parseByParams(params.by),
    ...parseHullParams(params.hullMaterial),
    ...parseOwnerParams(params.owner),
    ...parseKeywordParams(params.keyword),
    ...parseRepParams(params.rep),
    ...parseEngineNumberParams(params.numberOfEngines),
    ...parseEngineOneHoursParams(params.engineOneHours)
  };

  let defaults = {
    ...boatConstants.DEFAULT_REFINE_SEARCH
  };
  defaults = Object.assign(defaults, parsed);

  return defaults;
};

/**
 * Memoizes URL parameters and their matched values to avoid redundant computations.
 * It uses the first parameter (URL) as the key and the second parameter (matched params)
 * If the same URL is passed twice, it will return the same
 * object regardless of the second parameter.
 *
 * @returns {Function} A function that takes a URL and matched params, and returns the memoized params.
 */
export const memoizedUrlMatch = () => {
  const urlMatchedParams = {};
  const emptyParamsKey = '__default_empty_params__';
  const defaultParams = (url, urlMatch) => {
    if (!url) {
      return (
        urlMatchedParams[emptyParamsKey] || defaultParams(emptyParamsKey, null)
      );
    }
    if (urlMatchedParams[url]) {
      return urlMatchedParams[url];
    }
    const params = getDefaultParams(definedValue(urlMatch, {}));
    urlMatchedParams[url] = params;
    return params;
  };
  return defaultParams;
};

/**
 * This function memoizes by the first parameter, it uses the seccond to deal with the matched params.
 * BEWARE!!!!!
 * If we pass the same url twice, we will get the same object no matter the second parameter.
 * @type {(function(*, *): (*))|*}
 */
export const defaultParamsMemoized = memoizedUrlMatch();

// alias to make it readable in filters
// we should get rid of getDefaultParams and use defaultParamsMemoized so we avoid executing them a lot.
// we should follow the same approach with generateSearchPath
export const stateFromUrlMatch = defaultParamsMemoized;

// Litlle by little we should be replacing getActiveParams with this function. We should also fix the wrong equality to deal with active params
// multifacetedBoatClass is not active and should be empty if we don't have it in params argument.
// This function should return an empty object when params is empty.
// all accross the portal we call getActiveParams, and we deal with the bad definition of it since the beginning of times.
export const getActiveParamsMemo = (params) => {
  const defaults = defaultParamsMemoized();
  const active = Object.entries(params).filter(([key, value]) => {
    return !isEqual(defaults[key], value);
  });

  const allActive = active.reduce(
    (object, [key, value]) => ({ ...object, [key]: value }),
    {}
  );
  return allActive;
};

// There is an evil filter below that is filtering out empty values due to strict comparison.
// We are calling this function in lots of places, we should get rid of it and use getActiveParamsMemo.
// Also, the reduce seems unnecessary.
export const getActiveParams = (params) => {
  const defaults = getDefaultParams({});
  const active = Object.entries(params).filter(([key, value]) => {
    return defaults[key] !== value;
  });

  const allActive = active.reduce(
    (object, [key, value]) => ({ ...object, [key]: value }),
    {}
  );

  return allActive;
};

export const getNotEmptyActiveParams = (params) => {
  const activeParams = getActiveParams(params);

  const filteredParams = {};
  for (const key in activeParams) {
    if (Array.isArray(activeParams[key]) && !activeParams[key].length) {
      continue;
    }

    if (
      typeof activeParams[key] === 'object' &&
      !Object.keys(activeParams[key]).length
    ) {
      continue;
    }

    if (!activeParams[key]) {
      continue;
    }

    filteredParams[key] = activeParams[key];
  }

  return filteredParams;
};

/**
 * @param selectedMakeSlug {string} - Selected make slug
 * @param selectedMakeModels {{[string]: [string]}} - Selected makes and models map
 * @param makeModel {[{value: string, count: string, model: [{value: string, count: string}]}]} - Available makes and model `value` is the make's formatted name
 * @returns {null|string}
 */
export const getFormattedMakeForFacet = (
  selectedMakeSlug,
  selectedMakeModels = {},
  makeModel
) => {
  if (!makeModel) {
    return null;
  }
  const selectedMake = makeModel.find(({ value: makeFormattedName }) => {
    const slugMake = normalizeMakeModel(
      hyphenateUrlComponents(makeFormattedName)
    );
    return has(selectedMakeModels, slugMake) && slugMake === selectedMakeSlug;
  });
  return get(selectedMake, 'value');
};

/**
 * @param facetKey {"city"}
 * @param selectedValues {[string]} - Facet value slugs
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @returns {array}
 */
const formatCity = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams
) => {
  return selectedValues.map((facetValue) => {
    let newValueSlug = without(selectedValues, facetValue);
    const formattedValue = unhyphenateUrlComponents(facetValue);

    const indexKey = `${facetKey}.${facetValue}`;
    const href = generateSearchPath(
      { ...noFacet, [facetKey]: newValueSlug },
      defaultParams,
      true
    );
    return {
      ...facetMeta,
      href,
      key: facetKey,
      indexKey,
      value: formattedValue,
      newValue: newValueSlug
    };
  });
};

// TODO: Move below files to a common place to keep the code clean
/**
 * @param facetKey {"fuelType" | "hullMaterial"}
 * @param currentValue {string} - Current facet
 * @param selectedValues {[string]} - Facet value slugs
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @returns {array}
 */
const formatFuelHull = (
  facetKey,
  currentValue,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  i18Service = getDefaultI18Service()
) => {
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  const formattedValue = t(messages[`${facetKey}FacetText`][currentValue]);
  const indexKey = `${facetKey}.${currentValue}`;
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: selectedValues },
    defaultParams,
    true
  );
  return {
    ...facetMeta,
    href: href,
    key: facetKey,
    indexKey,
    value: formattedValue,
    newValue: selectedValues
  };
};

/**
 * @param facetKey { "makeModel" | "multiFacetedBoatTypeClass" }
 * @param selectedValues {{[string]: [string]}} - Facet values map. Type => [Class] or Make => [Model]
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @param facetValue {string} - Selected Make or Type slug
 * @param subFacetValue {string} - Selected Model or Class slug
 * @param formattedMake {string} - Formatted Make name
 * @param makeModel {[{value: string, count: string, model: [{value: string, count: string}]}]} - Available makes and model `value` is the make's formatted name
 * @returns {object}
 */
// eslint-disable-next-line max-len
const formatModelClass = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  facetValue,
  subFacetValue,
  formattedMake,
  makeModel,
  i18Service = getDefaultI18Service()
) => {
  const newValueSlug = {
    ...omit(selectedValues, facetValue),
    ...toggleSubFacet(facetValue, subFacetValue, selectedValues)
  };
  let formattedValue;
  let isFormatted;
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  if (includes(getBoatConstantsFromI18n(i18Service).allTypes, subFacetValue)) {
    // Case multiFacetedBoatTypeClass: subfacet = all-*
    formattedValue = `${t(messages.allType, {
      boatType: t(messages[facetValue])
    })}`;
    isFormatted = true;
  } else if (facetKey === 'multiFacetedBoatTypeClass') {
    const formattedClass = has(messages.classFacetValues, subFacetValue)
      ? t(messages.classFacetValues[subFacetValue])
      : unslugify(subFacetValue);
    formattedValue = `${t(messages[facetValue])} - ${formattedClass}`;
    isFormatted = has(messages.classFacetValues, subFacetValue);
  } else {
    // Case makeModel
    const makeObj = makeModel.find(
      (e) => hyphenateUrlComponents(e.value) === facetValue
    ) || { value: unslugify(facetValue), model: [] };
    const modelObj = makeObj.model.find(
      (e) => hyphenateUrlComponents(e.value) === subFacetValue
    ) || { value: unslugify(subFacetValue) };
    formattedValue = `${formattedMake || makeObj.value} - ${modelObj.value}`;
    isFormatted = !!formattedMake;
  }

  const indexKey = `${facetKey}.${facetValue}.${subFacetValue}`;
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );

  return {
    ...facetMeta,
    href,
    key: facetKey,
    indexKey,
    value: formattedValue,
    newValue: newValueSlug,
    isFormatted
  };
};

/**
 * @param facetKey { "makeModel" }
 * @param selectedValues {{[string]: [string]}} - Facet values map. Make => [Model]
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @param facetValue {string} - Selected Make
 * @param formattedMake {string} - Formatted Make name
 * @param seoMakeInfo {[{make: string, seoMakeName: string}]} - Alternative make name suitable for SEO content
 * @returns {object}
 */
const formatMake = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  facetValue,
  formattedMake,
  seoMakeInfo,
  i18Service = getDefaultI18Service()
) => {
  const newValueSlug = omit(selectedValues, facetValue);
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  let formattedValue;
  if (!isEmpty(seoMakeInfo) && seoMakeInfo.make === facetValue) {
    formattedValue = `${t(messages.all)} ${seoMakeInfo.seoMakeName}`;
  } else {
    formattedValue = `${t(messages.all)} ${translateMake(
      formattedMake || unslugify(facetValue)
    )}`;
  }
  const isFormatted = !!formattedMake;
  const indexKey = `${facetKey}.${facetValue}`;
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );

  return {
    ...facetMeta,
    href,
    key: facetKey,
    indexKey,
    value: formattedValue,
    newValue: newValueSlug,
    isFormatted
  };
};

/**
 * @param facetKey {"subdivision"}
 * @param selectedValues {string} - The subdivision initials
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @returns {object}
 */
const formatSubdivision = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  selectedCountry = get(getConfig(), 'country'),
  i18Service = getDefaultI18Service()
) => {
  const newValueSlug = defaultParams[facetKey];
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );
  let formattedValue;
  if (
    get(messages, `countrySubdivision.${selectedCountry}.${selectedValues}`)
  ) {
    formattedValue = t(
      get(messages, `countrySubdivision.${selectedCountry}.${selectedValues}`)
    );
  }
  const isFormatted = true;
  return {
    ...facetMeta,
    key: facetKey,
    href,
    value: formattedValue,
    newValue: newValueSlug,
    isFormatted
  };
};

/**
 * @param facetKey {"region"}
 * @param selectedValues {string} - The region selected
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @returns {object}
 */
const formatRegion = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  i18Service = getDefaultI18Service()
) => {
  const newValueSlug = defaultParams[facetKey];
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );
  const formattedValue = t(messages.countryRegions[selectedValues]);
  const isFormatted = true;
  return {
    ...facetMeta,
    key: facetKey,
    href,
    value: formattedValue,
    newValue: newValueSlug,
    isFormatted
  };
};

/**
 * @param facetKey {"country"}
 * @param selectedValues {string} - The country selected
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @returns {object}
 */
const formatCountry = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  i18Service = getDefaultI18Service()
) => {
  const newValueSlug = defaultParams[facetKey];
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );
  const formattedValue = t(messages.countries[selectedValues]);
  const isFormatted = true;
  return {
    ...facetMeta,
    key: facetKey,
    href,
    value: formattedValue,
    newValue: newValueSlug,
    isFormatted
  };
};

/**
 * @param facetKey {"numberOfEngines"}
 * @param selectedValues {string} - The number of engines selected
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ formattedKey: string }} - Translated facet name
 * @param defaultParams {object} - Map of facets with default values
 * @returns {object}
 */
const formatEnginesNumber = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams
) => {
  const formattedValue =
    parseInt(selectedValues) ===
    getBoatsConstants().SRP_MAXIMUM_ENGINES_NUMBER_SEARCH
      ? `${selectedValues}+`
      : selectedValues;
  const isFormatted = true;
  const newValueSlug = defaultParams[facetKey];
  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );

  return {
    ...facetMeta,
    key: facetKey,
    href,
    value: formattedValue,
    newValue: newValueSlug,
    isFormatted
  };
};

/**
 * @param facetKey {string} - condition, length, radius, year, postalCode, etc
 * @param selectedValues {string | { max: string, min: string }} - Facet value
 * @param noFacet {{[string]: ( [string] | object | string )}} - Map of active facets without current facet
 * @param facetMeta {{ prefix?: string, suffix?: string, format?: string, formattedKey: string }}
 * @param defaultParams {object} - Map of facets with default values
 * @returns {object}
 */
const formatDefaultFacets = (
  facetKey,
  selectedValues,
  noFacet,
  facetMeta,
  defaultParams,
  i18Service = getDefaultI18Service()
) => {
  const newValueSlug = defaultParams[facetKey];
  let isFormatted = false;
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  let { prefix = '', suffix = '' } = facetMeta;

  if (isObject(selectedValues)) {
    // Case length, year or price
    if (selectedValues.min && !selectedValues.max) {
      suffix = '+ ' + suffix;
    } else if (!selectedValues.min && selectedValues.max) {
      prefix = `${t(messages.upTo)} ${prefix}`;
    } else if (!isEmpty(suffix)) {
      suffix = ' ' + suffix;
    }
  }
  let formattedValue =
    isString(selectedValues) && facetKey !== 'keyword'
      ? unslugify(selectedValues)
      : selectedValues;
  if (
    facetKey === 'condition' ||
    facetKey === 'forSale' ||
    facetKey === 'group'
  ) {
    formattedValue = t(messages[selectedValues]);
    isFormatted = true;
  }
  if (facetKey === 'radius' && has(messages, selectedValues)) {
    formattedValue = t(messages[selectedValues]);
    isFormatted = true;
  }
  if (facetKey === 'sort') {
    const [sortBy, sortOrder] = selectedValues.split(':');
    if (sortBy in messages) {
      formattedValue = `${t(messages[sortBy])}:${t(
        messages.sortFacetValues[sortOrder]
      )}`;
      isFormatted = true;
    }
  }
  if (facetKey === 'length') {
    isFormatted = true;
  }
  if (facetKey === 'keyword') {
    isFormatted = true;
  }

  if (facetKey === 'currency') {
    formattedValue = formattedValue && formattedValue.toUpperCase();
  }
  if (facetKey === 'latlon') {
    const latLonDecimals = 3;
    formattedValue = shortenB64latlon(selectedValues, latLonDecimals);
    facetMeta.formattedKey = t(messages.location);
    formattedValue = t(messages.radius.myLocation);
  }

  const href = generateSearchPath(
    { ...noFacet, [facetKey]: newValueSlug },
    defaultParams,
    true
  );

  return {
    ...facetMeta,
    href,
    key: facetKey,
    value: formattedValue,
    newValue: newValueSlug,
    prefix,
    suffix,
    isFormatted
  };
};

/**
 * @param active {{[string]: ([string] | object | string) }} - Map of active facets
 * @param makeModel {[{value: string, count: string}]} - Selected makes. and models `value` is the make's formatted name
 * @param seoMakeInfo {[{make: string, seoMakeName: string}]} - Alternative make name suitable for SEO content
 * @returns {array}
 */
export const getFormattedParamsArray = (
  active,
  makeModel = [],
  seoMakeInfo = {},
  customUom,
  i18Service = getDefaultI18Service()
) => {
  const currency = active.currency;
  const t = getFormatMessageFunction(i18Service);
  const messages = getMessages();
  const facetMetaMap = {
    radius: {
      suffix: getRadius(),
      format: 'number'
    },
    price: {
      [getCurrencyFormat() === 'postfix'
        ? 'suffix'
        : getCurrencyFormat(currency)]: getCurrencySymbol(currency),
      format: 'number'
    },
    length: {
      suffix: getLengthSymbol(customUom?.length),
      format: 'number'
    }
  };
  const defaultParams = getDefaultParams({});
  return flatten(
    Object.entries(active).map(([facetKey, selectedValues]) => {
      const facetMeta = {
        ...facetMetaMap[facetKey],
        formattedKey: t(messages[facetKey])
      };
      const noFacet = omit(active, facetKey);
      switch (facetKey) {
        case 'country':
          return formatCountry(
            facetKey,
            selectedValues,
            omit(noFacet, ['city', 'region', 'subdivision']),
            facetMeta,
            defaultParams
          );
        case 'city':
          return formatCity(
            facetKey,
            selectedValues,
            noFacet,
            facetMeta,
            defaultParams
          );
        case 'engine':
        case 'fuelType':
        case 'hullMaterial':
          return selectedValues.map((facetValue) => {
            const filteredFacets = filter(selectedValues, (val) => {
              return val !== facetValue;
            });
            return formatFuelHull(
              facetKey,
              facetValue,
              filteredFacets,
              noFacet,
              facetMeta,
              defaultParams
            );
          });
        case 'makeModel':
        case 'multiFacetedBoatTypeClass':
          // typeof selectedValues = { type: [class] } or { make: [model] }
          return flatten(
            Object.entries(selectedValues).map(([facetValue, subFacets]) => {
              // Case makeModel: facetValue is a Make
              const formattedMake = getFormattedMakeForFacet(
                facetValue,
                active.makeModel,
                makeModel
              );
              if (subFacets && subFacets.length) {
                return subFacets.map((subFacetValue) =>
                  formatModelClass(
                    facetKey,
                    selectedValues,
                    noFacet,
                    facetMeta,
                    defaultParams,
                    facetValue,
                    subFacetValue,
                    formattedMake,
                    makeModel
                  )
                );
              }
              // Case makeModel: There is always subfacets for type/class
              return formatMake(
                facetKey,
                selectedValues,
                noFacet,
                facetMeta,
                defaultParams,
                facetValue,
                formattedMake,
                seoMakeInfo
              );
            })
          );
        case 'subdivision':
          return formatSubdivision(
            facetKey,
            selectedValues,
            omit(noFacet, ['city']),
            facetMeta,
            defaultParams,
            get(active, 'country')
          );
        case 'region':
          // Removing the region should clear also the subdivision
          return formatRegion(
            facetKey,
            selectedValues,
            omit(noFacet, ['subdivision', 'city']),
            facetMeta,
            defaultParams
          );
        case 'numberOfEngines':
          return formatEnginesNumber(
            facetKey,
            selectedValues,
            noFacet,
            facetMeta,
            defaultParams
          );
        default:
          return formatDefaultFacets(
            facetKey,
            selectedValues,
            noFacet,
            facetMeta,
            defaultParams
          );
      }
    })
  );
};
/* eslint-enable max-len */

/**
 * @param params {{[string]: ([string] | object | string) }} - Map of facets with default values
 * @param getActive {boolean} - If true, get all active parameters before counting
 * @param excludePageParams {boolean} - If true, counts the number of active parameters excluding all page related ones
 * @returns {array}
 */
export const getActiveParamsLength = (
  params,
  getActive = true,
  excludePageParams = true
) => {
  const clonedParams = { ...params };
  if (excludePageParams) {
    delete clonedParams.page;
    delete clonedParams.pageSize;
  }

  if (
    clonedParams.multiFacetedBoatTypeClass &&
    isEmpty(clonedParams.multiFacetedBoatTypeClass)
  ) {
    delete clonedParams.multiFacetedBoatTypeClass;
  }

  const activeParams = getActive ? getActiveParams(clonedParams) : clonedParams;
  return Object.keys(activeParams).length;
};

export const getFormattedParams = (
  active,
  makeModel,
  seoMakeInfo,
  customUom
) => {
  if (getActiveParamsLength(active, false, true) === 1) {
    return getFormattedParamsArray(active, makeModel, seoMakeInfo, customUom);
  }
  return getFormattedParamsArray(active, makeModel, undefined, customUom);
};

/**
 * We clear subdivision code from the subdivision for all the languages with a custom localized subdivision.
 * @param {string} subdivision
 * @param {Object} msgs messages passed to make tests easily
 * @param {function} formatMsg intl.formatMessage by default, or any formatter we want
 * @returns {string}
 */
export const swapLocalizedSubdivision = (
  subdivision,
  msgs,
  formatMsg,
  i18Service
) => {
  const subdivisionConstants = getBoatConstantsFromI18n(i18Service);
  const countryCodes = ['US', 'CA', 'ES', 'IT'];
  countryCodes.forEach((code) => {
    const patternKey = `SEARCH_URL_SUBDIVISION_${code}_PATTERN`;
    const pattern = subdivisionConstants[patternKey];
    if (pattern.test(subdivision)) {
      const msg = msgs.search[`subdivision${code}`];
      subdivision = subdivision.replace(
        formatMsg(msg),
        formatMsg(msgs.search.subdivision)
      );
    }
  });
  return subdivision;
};

/**
 * This function has been moved to avoid circular dependencies.
 * We re-export to keep imports.
 * TODO: Fix imports and remove this re-export
 * @param string
 * @param removeSpecialCharacters
 * @returns {string|*}
 */
export const hyphenateUrlComponents = (
  string,
  removeSpecialCharacters = true
) => {
  return sharedHyphenate(string, removeSpecialCharacters);
};

export const singleHyphenateUrlComponents = (
  string,
  removeSpecialCharacters = true
) => {
  if (!isString(string)) {
    return '';
  }
  string = string.toLowerCase();
  // Remove accents and special characters
  if (removeSpecialCharacters) {
    string = normalizeString(string);
    const validChars = new RegExp(
      `[^${getBoatsConstants().ALLOWED_CHARACTERS_IN_URL}]+`,
      'g'
    );
    string = string.replace(validChars, ' ');
  }
  string = string.replace(/[ ]+/g, ' ');
  string = string.split('&').join('$');

  string = slugify(string, '-');
  string = string.replace(/-$/, '');
  string = string.replace(/^-/, '');
  return string;
};

export const reduceURLForRedirect = (props) => {
  let params = get(props, 'match.params', {});
  let lastParam;
  let modified = false;
  const boatsContstants = getBoatConstantsFromI18n();
  forEach(params, (value, key) => {
    if (!boatsContstants.INDEXABLE_PARAMS.includes(key)) {
      if (params[key]) {
        params[key] = undefined;
        modified = true;
      }
    }
  });
  let reducedUrl = generateSearchPath({}, getDefaultParams(params), true);
  if (!modified) {
    boatsContstants.INDEXABLE_PARAMS.forEach((matchParam) => {
      if (params[matchParam]) {
        lastParam = matchParam;
      }
    });
    delete params[lastParam];
    reducedUrl = generateSearchPath(
      {},
      getDefaultParams(params, lastParam),
      true
    );
  }

  return reducedUrl;
};

export const searchPathIsActive = (url) =>
  !!matchPath(decodeURI(url), getBoatConstantsFromI18n().SEARCH_URL_PATTERN);

export const searchBrandedPathIsActive = (url) =>
  !!matchPath(
    decodeURI(url),
    getBoatConstantsFromI18n().BRANDED_SEARCH_URL_PATTERN
  );

export const getSearchPathUrl = (searchPath, host, { tld, type, domain }) => {
  let url = host;

  if (tld) {
    const currentTld = '.' + getTld(host);
    url = url.replace(currentTld, tld);
  }

  if (type === 'domain') {
    const prevDomain = getDomain(host);
    url = url.replace(prevDomain, domain);
  }

  return `${url}${searchPath}`;
};

export const redirectPartialMakeNameToFullMakeName = (
  props,
  fullMakeNameUrlParam,
  i18nService
) => {
  let params = get(props, 'match.params', {});
  const boatConstants = getBoatConstantsFromI18n(i18nService);
  forEach(params, (value, key) => {
    if (!boatConstants.INDEXABLE_PARAMS.includes(key)) {
      if (params[key]) {
        params[key] = undefined;
      }
    }
  });
  set(params, 'make', fullMakeNameUrlParam);
  return generateSearchPath({}, getDefaultParams(params), true);
};

export const isBDP = (url, language) => {
  const formatMessage = getFormatMessageFunction();
  const messages = getMessages();
  const boatPath = formatMessage(messages.detailsRoot, undefined, language);
  const boatPathRegex = new RegExp(`^(/[a-zA-Z]{2})?/${boatPath}/.*[0-9]+/$`);
  return boatPathRegex.test(url);
};

export const redirectBDPWrongUrls = (redirectPath, redirectCode, language) => {
  if (redirectPath.includes('/?')) {
    redirectPath = redirectPath.replace('/?', '-');
  }
  const redirectPathCount = redirectPath.split('/').length - 1;
  if (redirectPathCount > 3) {
    //URLs that include the language are /XX/ so we need to start counting from postion 4.
    const slashIndex = redirectPathCount === 5 ? 4 : 1;
    const redirectPathFirstPart = redirectPath.substring(
      0,
      redirectPath.indexOf('/', slashIndex)
    );
    if (redirectPathCount === 4 && redirectPathFirstPart.includes(language)) {
      return {
        redirectPath: redirectPath,
        redirectCode: redirectCode
      };
    }

    let redirectPathSubstring = redirectPath.substring(
      redirectPath.indexOf('/', slashIndex) + 1,
      redirectPath.lastIndexOf('/')
    );
    redirectPathSubstring = redirectPathSubstring.replace(/\//g, '-');
    return {
      redirectPath: `${redirectPathFirstPart}/${redirectPathSubstring}/`,
      redirectCode: 301
    };
  }
  return {
    redirectPath: redirectPath,
    redirectCode: redirectCode
  };
};

export const searchPathByKeyword = (keyword) => {
  if (typeof keyword !== 'string' || !keyword) {
    return null;
  }
  const searchParam = {keyword};
  return boatsUrlGenerate(searchParam);
};
